/**
 *
 * CheckUserEmail
 *
 */

import React, { memo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

// styles
import './style.scss';

import { flushStateCheckUserEmail, changeCheckUserEmail } from './actions';
import {
  selectCheckUserEmailData,
  selectCheckUserEmailLoading,
  selectCheckUserEmailError,
} from './selectors';

import { Form, Input } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import { useRefState } from 'utils/hooks';

export function CheckUserEmail({
  method,
  // param
  item,
  // props
  loadingCheckEmail,
  checkUserEmailData,
  // actions
  flushState,
  changeCheckUserEmail,
  // default
  className,
}) {
  useInjectReducer({ key: 'checkUserEmail', reducer });
  useInjectSaga({ key: 'checkUserEmail', saga });

  const childClassNames = classNames(
    'item-form-wrapper',
    'check-user-email',
    className,
  );

  const [validate, validateRef, setValidate] = useRefState(false);

  useEffect(() => {
    // --> main code here

    // Anything in here is fired on component unmount.
    return () => {
      // Clearing the state after unmounting a component
      flushState();
    };
  }, []);

  /**
   * Check validate status from backend
   */
  useEffect(() => {
    if (checkUserEmailData && !loadingCheckEmail) {
      if (checkUserEmailData?.code == 200) {
        setValidate(true);
      } else if (checkUserEmailData?.code == 401) {
        setValidate(false);
      }
    }
  }, [checkUserEmailData, loadingCheckEmail]);

  const validateEmails = async $email => {
    const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!emailRegex.test($email)) {
      return false;
    } else {
      return true;
    }
  };

  const validateEmail = email => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      );
  };

  const checkEmails = useCallback(
    async (rule, value, callback) => {
      const isValid = await validateEmails(value);
      isValid && (await changeCheckUserEmail({ email: value }));

      if (value && isValid) {
        if (!loadingCheckEmail) {
          if (checkUserEmailData?.code == 200 || validateRef?.current) {
            return Promise.resolve(true);
          } else if (checkUserEmailData?.code != 200 && !validateRef?.current) {
            return Promise.reject(
              new Error("L'utilisateur avec cet e-mail existe déjà."),
            );
          }
        }
      } else if (value && !isValid) {
        return Promise.reject(new Error("L'entrée n'est pas valide. E-mail!"));
      } else {
        return Promise.resolve(false);
      }
    },
    [checkUserEmailData, loadingCheckEmail],
  );

  const emailRules = [
    {
      required: item.required || false,
      message: 'Champs obligatoire',
      validateTrigger: 'onSubmit',
    },
  ];

  if (method !== undefined && method == 'nouvel') {
    emailRules.push({
      type: 'email',
      validateTrigger: 'onSubmit',
      validator: checkEmails,
    });
  }

  // render function
  return (
    item && (
      <Form.Item
        key={item.name + '-item'}
        label={item.displayLabel ? item.label : item.label}
        name={item.name}
        className={item.name + '-wrapper ' + childClassNames}
        initialValue={item?.value}
        rules={emailRules}
      >
        <Input
          type="email"
          placeholder={
            item.placeholder !== undefined ? item.placeholder : item.label
          }
        />
      </Form.Item>
    )
  );
}

CheckUserEmail.defaultProps = {
  method: 'update',
};

CheckUserEmail.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loadingCheckEmail: PropTypes.bool,
  changeCheckUserEmail: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  loadingCheckEmail: selectCheckUserEmailLoading(),
  error: selectCheckUserEmailError(),
  checkUserEmailData: selectCheckUserEmailData(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushStateCheckUserEmail()),
    changeCheckUserEmail: arrayData =>
      dispatch(changeCheckUserEmail(arrayData)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(CheckUserEmail);
